// Third-party libraries that define a global (window.SpeechSDK, window.WaveSurfer, window.OpusMediaRecorder). They
// are the same browser builds index.html used to load from public CDNs on every page (two of them unversioned, as
// @latest), now pinned in package.json and served with the app: Vite copies each file into the build under a
// content hash. They load with the pages that use them (see App.svelte), so those pages never start without them.
import speechSdkUrl from 'microsoft-cognitiveservices-speech-sdk/distrib/browser/microsoft.cognitiveservices.speech.sdk.bundle-min.js?url';
import waveSurferUrl from 'wavesurfer.js/dist/wavesurfer.min.js?url';
import waveSurferTimelineUrl from 'wavesurfer.js/dist/plugin/wavesurfer.timeline.min.js?url';
import opusRecorderUrl from 'opus-media-recorder/OpusMediaRecorder.umd.js?url';
import opusWorkerUrl from 'opus-media-recorder/encoderWorker.umd.js?url';
import oggOpusWasmUrl from 'opus-media-recorder/OggOpusEncoder.wasm?url';
import webmOpusWasmUrl from 'opus-media-recorder/WebMOpusEncoder.wasm?url';

const loading = new Map();

/** Runs a classic script (one that defines a global) once; later calls share the first load. */
export function loadScript(src) {
  if (!loading.has(src)) {
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`could not load ${src}`));
      document.head.appendChild(script);
    });
    // a failed load may be retried (e.g. after the connection came back)
    promise.catch(() => loading.delete(src));
    loading.set(src, promise);
  }
  return loading.get(src);
}

/** Azure Speech SDK (window.SpeechSDK), for text to speech and streamed recognition. */
export function loadSpeechSdk() {
  return loadScript(speechSdkUrl).then(() => window.SpeechSDK);
}

/** wavesurfer.js 6 with its timeline plugin (window.WaveSurfer). */
export function loadWaveSurfer() {
  return loadScript(waveSurferUrl)
    .then(() => loadScript(waveSurferTimelineUrl))
    .then(() => window.WaveSurfer);
}

const absolute = (url) => new URL(url, document.baseURI).href;

/**
 * The Opus recorder (window.OpusMediaRecorder). Its encoder worker script runs in the page as well, as it did from
 * the CDN: the recorder then starts the worker from a copy of that code instead of downloading it again. The two
 * WebAssembly encoders are fetched ahead, as the app did at startup, so the first recording does not wait for them.
 */
export function loadOpusRecorder() {
  return loadScript(opusRecorderUrl)
    .then(() => loadScript(opusWorkerUrl))
    .then(() => {
      for (const url of [oggOpusWasmUrl, webmOpusWasmUrl]) fetch(url).catch(() => {});
      return window.OpusMediaRecorder;
    });
}

/**
 * The recorder's worker options. Absolute URLs: the worker runs from a blob: URL, against which a path like
 * /assets/... cannot be resolved.
 */
export function opusWorkerOptions() {
  return {
    OggOpusEncoderWasmPath: absolute(oggOpusWasmUrl),
    WebMOpusEncoderWasmPath: absolute(webmOpusWasmUrl),
  };
}
